// The standard document actions (spec/dashboard.md section 6.1, research/01 P17) for cards and rows: the menu items and
// the dialogs they open, over the workspace.
import { useState, type ReactNode } from 'react';
import type { Document, Json } from '../api/types';
import { useToasts, type Toasts } from '../ui/Toast';
import { useWorkspaceVersion } from '../workspace/react';
import { isTempId, type Workspace } from '../workspace/Workspace';
import { baseOf, cloneDocument } from './actions';
import { ChangeTemplateDialog } from './dialogs/ChangeTemplateDialog';
import { CopyToBranchDialog } from './dialogs/CopyToBranchDialog';
import { ReplaceByDialog } from './dialogs/ReplaceByDialog';
import { useCreateFor } from './overrides/useCreateFor';
import { useEditorHost, useWidgetEnv } from './widgets/context';

export interface MenuItemSpec {
  key: string;
  label: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  separator?: boolean;
  danger?: boolean;
}

export interface DocumentActions {
  /** the ⋮ items of one document */
  items: (doc: Document, extra?: { calendarColor?: boolean; onDone?: () => void }) => MenuItemSpec[];
  /** the dialogs currently open; render them once in the page */
  dialogs: ReactNode;
  edit: (doc: Document, path: string, value: unknown) => void;
  remove: (docs: Document[]) => Promise<void>;
  clone: (doc: Document) => string;
}

export interface StagedDeletion {
  id: string;
  label: string;
  template: string;
  base?: { version: number | null; value: Json };
}

/**
 * Stages the deletion of documents at once (section 6.1 Delete: the deletion is staged, Discard / undo reverts it, so
 * there is no "Stage deletion?" dialog) and toasts `Deletion of <label> staged` with Undo. Undo reverts exactly when
 * nothing was staged since (which also brings back a dropped new draft); otherwise it restores each key it deleted.
 */
export function stageDeletion(workspace: Workspace, toasts: Toasts, items: readonly StagedDeletion[]): void {
  if (items.length === 0) return;
  const before = workspace.mark();
  for (const it of items) workspace.remove('document', it.id, it.base, { label: it.label, template: it.template });
  const after = workspace.mark();
  const undo = () => {
    if (workspace.mark() === after) workspace.revertTo(before);
    else for (const it of items) workspace.restore('document', it.id);
  };
  const message = items.length === 1 ? `Deletion of ${items[0]!.label} staged` : `Deletion of ${items.length} documents staged`;
  toasts.success(message, { actions: [{ label: 'Undo', onClick: undo }] });
}

export function useDocumentActions(opts: { canWrite: boolean; serverOf?: (doc: Document) => Document; onReload?: () => void } ): DocumentActions {
  const { catalog, workspace } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const host = useEditorHost();
  const toasts = useToasts();
  const [dialog, setDialog] = useState<{ kind: 'replace'; doc: Document } | { kind: 'copy' | 'change'; docs: Document[] } | null>(null);
  const createFor = useCreateFor();
  const serverOf = opts.serverOf ?? ((doc: Document) => doc);
  const edit = (doc: Document, path: string, value: unknown) => {
    workspace.edit('document', doc.id, path, value, { base: baseOf(serverOf(doc)), label: catalog.label(doc), template: doc.template });
  };
  // section 6.1 Delete: staged at once, no dialog; the toast's Undo restores what this action staged
  const remove = async (docs: Document[]) => {
    stageDeletion(workspace, toasts, docs.map((doc) => ({ id: doc.id, label: catalog.label(doc), template: doc.template, base: baseOf(serverOf(doc)) })));
  };
  const clone = (doc: Document) => {
    const id = cloneDocument(workspace, catalog, doc);
    toasts.success('Copy staged.');
    return id;
  };
  const items = (doc: Document, extra: { calendarColor?: boolean; onDone?: () => void } = {}): MenuItemSpec[] => {
    const temp = isTempId(doc.id);
    const canWrite = opts.canWrite;
    return [
      { key: 'open', label: 'Open document', onSelect: () => void host?.open(doc.id), disabled: !host },
      { key: 'clone', label: 'Clone', onSelect: () => void host?.open(clone(doc)), disabled: !canWrite },
      { key: 'export', label: doc.export ? 'Export: on → off' : 'Export: off → on', onSelect: () => edit(doc, 'export', !doc.export), disabled: !canWrite },
      ...(extra.calendarColor ? [{ key: 'colour', label: 'Calendar colour…', onSelect: () => void pickColour(doc), disabled: !canWrite }] : []),
      { key: 'replace', label: 'Replace by…', onSelect: () => setDialog({ kind: 'replace', doc: serverOf(doc) }), disabled: !canWrite || temp },
      { key: 'copy', label: 'Copy to branch…', onSelect: () => setDialog({ kind: 'copy', docs: [serverOf(doc)] }), disabled: temp },
      { key: 'change', label: 'Change template…', onSelect: () => setDialog({ kind: 'change', docs: [serverOf(doc)] }), disabled: !canWrite || temp },
      { key: 's1', label: '', separator: true },
      { key: 'override', label: 'Create override…', onSelect: () => createFor.open('override', doc), disabled: !canWrite },
      { key: 'abtest', label: 'Create A/B test…', onSelect: () => createFor.open('abtest', doc), disabled: !canWrite },
      { key: 's2', label: '', separator: true },
      { key: 'delete', label: 'Delete', onSelect: () => void remove([doc]), danger: true, disabled: !canWrite },
    ];
  };
  const pickColour = async (doc: Document) => {
    const current = ((doc.meta ?? {}) as { calendarColor?: string }).calendarColor ?? '';
    const next = window.prompt('Calendar colour (#RRGGBB; empty for the automatic palette):', current);
    if (next === null) return;
    const meta = { ...((doc.meta ?? {}) as Record<string, unknown>) };
    if (next.trim() === '') delete meta.calendarColor;
    else if (/^#[0-9a-fA-F]{6}$/.test(next.trim())) meta.calendarColor = next.trim();
    else {
      toasts.error('A colour is #RRGGBB.');
      return;
    }
    edit(doc, 'meta', Object.keys(meta).length ? meta : null);
  };
  const dialogs = (
    <>
      {dialog?.kind === 'replace' && <ReplaceByDialog doc={dialog.doc} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'copy' && <CopyToBranchDialog docs={dialog.docs} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'change' && <ChangeTemplateDialog docs={dialog.docs} onClose={() => setDialog(null)} onDone={() => opts.onReload?.()} />}
      {createFor.dialog}
    </>
  );
  return { items, dialogs, edit, remove, clone };
}
